<template>
  <div class="ai-review">
    <div class="header">
      <div>
        <h2>AI Search Review</h2>
        <div class="subtitle">
          Every question learners ask the AI guide on /search, what it retrieved and answered, and what they clicked.
          Use it to tune retrieval and to spot content we should produce next.
        </div>
      </div>
      <div class="header-actions">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          size="small"
          range-separator="-"
          start-placeholder="Start"
          end-placeholder="End"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          style="width: 250px"
          @change="refreshAll"
        />
        <el-input
          v-model="reviewer"
          size="small"
          placeholder="Reviewer name"
          prefix-icon="el-icon-user"
          style="width: 150px"
          @change="saveReviewerName"
        />
        <el-button size="small" icon="el-icon-refresh" @click="refreshAll">Refresh</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab">
      <!-- ============================== Insights ============================== -->
      <el-tab-pane label="Insights" name="insights">
        <div v-loading="insightsLoading" class="insights">
          <template v-if="insights">
            <div class="kpi-grid">
              <div v-for="tile in kpiTiles" :key="tile.label" class="kpi">
                <div class="kpi-label">{{ tile.label }}</div>
                <div class="kpi-value">{{ tile.value }}</div>
                <div v-if="tile.hint" class="kpi-hint">{{ tile.hint }}</div>
              </div>
            </div>
            <div v-if="insights.totals.truncated" class="muted tiny truncated-note">
              Only the latest 5,000 turns in this range were aggregated — narrow the date range for exact numbers.
            </div>

            <el-row :gutter="16">
              <el-col :xs="24" :md="14">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header">
                    <span>Questions per day</span>
                    <span class="muted tiny">hover a bar for detail</span>
                  </div>
                  <div v-if="!insights.totals.turns" class="empty">No questions in this range.</div>
                  <div v-else class="bars" role="img" :aria-label="`Questions per day, ${dailySeries.length} days`">
                    <div
                      v-for="day in dailySeries"
                      :key="day.date"
                      class="bar-slot"
                      @mouseenter="hoverDay = day"
                      @mouseleave="hoverDay = null"
                    >
                      <div class="bar" :style="{ height: barHeight(day.turns) }" />
                    </div>
                  </div>
                  <div v-if="dailySeries.length" class="bar-axis">
                    <span>{{ dailySeries[0].date }}</span>
                    <span v-if="hoverDay" class="bar-tooltip">
                      <strong>{{ hoverDay.date }}</strong> · {{ hoverDay.turns }} questions · {{ hoverDay.weak }} weak / unhappy
                    </span>
                    <span>{{ dailySeries[dailySeries.length - 1].date }}</span>
                  </div>
                </el-card>
              </el-col>
              <el-col :xs="24" :md="10">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header"><span>Library coverage (AI judged)</span></div>
                  <div v-for="row in coverageRows" :key="row.key" class="coverage-row">
                    <el-tag size="mini" :type="row.tag" class="coverage-tag">{{ row.label }}</el-tag>
                    <div class="coverage-track">
                      <div class="coverage-fill" :class="`fill-${row.key}`" :style="{ width: row.pct + '%' }" />
                    </div>
                    <span class="coverage-num">{{ row.count }} · {{ row.pct }}%</span>
                  </div>
                  <div class="coverage-meta muted tiny">
                    Review status:
                    <span v-for="(count, status) in insights.review_counts" :key="status" class="review-count">
                      {{ reviewLabel(status) }} {{ count }}
                    </span>
                  </div>
                  <div class="coverage-meta muted tiny">
                    Reply languages:
                    <span v-for="(count, lang) in insights.languages" :key="lang" class="review-count">{{ lang }} {{ count }}</span>
                  </div>
                </el-card>
              </el-col>
            </el-row>

            <!-- Content ideas -->
            <el-card shadow="never" class="panel">
              <div slot="header" class="panel-header">
                <span>Content ideas from learner demand</span>
                <div class="panel-actions">
                  <el-select v-model="ideaLanguage" size="mini" style="width: 110px">
                    <el-option label="中文" value="zh" />
                    <el-option label="English" value="en" />
                  </el-select>
                  <el-select
                    v-if="ideaReports.length > 1"
                    v-model="activeReportId"
                    size="mini"
                    placeholder="History"
                    style="width: 190px"
                  >
                    <el-option
                      v-for="report in ideaReports"
                      :key="report.report_id"
                      :label="`${formatDate(report.created_at)} · ${report.question_count} Qs`"
                      :value="report.report_id"
                    />
                  </el-select>
                  <el-button size="mini" type="primary" :loading="ideasLoading" @click="generateIdeas">
                    Generate from this range
                  </el-button>
                </div>
              </div>
              <div v-if="!activeReport" class="empty">
                No report yet. It clusters weak-coverage and thumbs-down questions in the selected range into collection ideas.
              </div>
              <template v-else>
                <div class="muted tiny">
                  {{ formatDay(activeReport.date_from) }} – {{ formatDay(activeReport.date_to) }} ·
                  based on {{ activeReport.question_count }} questions · {{ activeReport.model }} ·
                  generated {{ formatDate(activeReport.created_at) }}
                </div>
                <p v-if="activeReport.summary" class="idea-summary">{{ activeReport.summary }}</p>
                <div class="idea-grid">
                  <div v-for="(idea, index) in activeReport.ideas" :key="index" class="idea">
                    <div class="idea-top">
                      <el-tag size="mini" :type="priorityTag(idea.priority)">{{ idea.priority || '—' }}</el-tag>
                      <span class="muted tiny">{{ idea.demand_signals || 0 }} questions</span>
                      <span v-if="idea.theme" class="muted tiny">· {{ idea.theme }}</span>
                    </div>
                    <div class="idea-title">{{ idea.title }}</div>
                    <div v-if="idea.why" class="idea-text">{{ idea.why }}</div>
                    <div v-if="idea.target_audience" class="idea-text"><strong>For:</strong> {{ idea.target_audience }}</div>
                    <details v-if="hasItems(idea.example_questions)">
                      <summary>Learner questions</summary>
                      <ul><li v-for="q in idea.example_questions" :key="q">{{ q }}</li></ul>
                    </details>
                    <details v-if="hasItems(idea.suggested_modules)">
                      <summary>Suggested modules</summary>
                      <ol><li v-for="m in idea.suggested_modules" :key="m">{{ m }}</li></ol>
                    </details>
                    <details v-if="hasItems(idea.reference_resources)">
                      <summary>Reference resources</summary>
                      <ul><li v-for="r in idea.reference_resources" :key="r">{{ r }}</li></ul>
                    </details>
                  </div>
                </div>
              </template>
            </el-card>

            <el-row :gutter="16">
              <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header">
                    <span>Content gaps by topic</span>
                    <span class="muted tiny">weak coverage, 👎 or marked as gap</span>
                  </div>
                  <el-table :data="insights.content_gaps" size="mini" max-height="360" empty-text="No gaps in this range">
                    <el-table-column label="Topic" min-width="130">
                      <template slot-scope="scope">
                        <el-button type="text" size="mini" @click="filterByTopic(scope.row.tag)">{{ scope.row.tag }}</el-button>
                      </template>
                    </el-table-column>
                    <el-table-column label="Weak Qs" prop="count" width="80" align="right" />
                    <el-table-column label="Example" min-width="240">
                      <template slot-scope="scope">
                        <div v-for="ex in scope.row.examples.slice(0, 2)" :key="ex.turn_id" class="example-line">
                          {{ ex.question }}
                        </div>
                      </template>
                    </el-table-column>
                  </el-table>
                </el-card>
              </el-col>
              <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header">
                    <span>Most-suggested outside resources</span>
                    <span class="muted tiny">what AI recommends that we don't have</span>
                  </div>
                  <el-table :data="insights.top_external" size="mini" max-height="360" empty-text="None yet">
                    <el-table-column label="Title" min-width="200">
                      <template slot-scope="scope">
                        <div class="strong">{{ scope.row.title }}</div>
                        <div class="muted tiny">{{ scope.row.author || '—' }} · {{ scope.row.type || 'resource' }}</div>
                      </template>
                    </el-table-column>
                    <el-table-column label="Times" prop="count" width="70" align="right" />
                    <el-table-column label="Clicks" prop="clicks" width="70" align="right" />
                  </el-table>
                </el-card>
              </el-col>
            </el-row>

            <el-row :gutter="16">
              <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header"><span>Top topics asked</span></div>
                  <el-table :data="insights.top_topics" size="mini" max-height="320" empty-text="None yet">
                    <el-table-column label="Topic" min-width="140">
                      <template slot-scope="scope">
                        <el-button type="text" size="mini" @click="filterByTopic(scope.row.tag)">{{ scope.row.tag }}</el-button>
                      </template>
                    </el-table-column>
                    <el-table-column label="Questions" prop="turns" width="90" align="right" />
                    <el-table-column label="Weak share" width="100" align="right">
                      <template slot-scope="scope">{{ percent(scope.row.weak_turns, scope.row.turns) }}</template>
                    </el-table-column>
                  </el-table>
                </el-card>
              </el-col>
              <el-col :xs="24" :lg="12">
                <el-card shadow="never" class="panel">
                  <div slot="header" class="panel-header"><span>Most-clicked library items</span></div>
                  <el-table :data="insights.top_clicked" size="mini" max-height="320" empty-text="No clicks yet">
                    <el-table-column label="Item" min-width="220">
                      <template slot-scope="scope">
                        <a :href="siteUrl(scope.row.path)" target="_blank" rel="noopener" class="item-link">{{ scope.row.title }}</a>
                        <div class="muted tiny">{{ scope.row.type }}</div>
                      </template>
                    </el-table-column>
                    <el-table-column label="Clicks" prop="clicks" width="80" align="right" />
                  </el-table>
                </el-card>
              </el-col>
            </el-row>
          </template>
        </div>
      </el-tab-pane>

      <!-- ============================== Conversations ============================== -->
      <el-tab-pane label="Conversations" name="turns">
        <el-form :inline="true" size="small" class="filters" @submit.native.prevent="searchTurns">
          <el-form-item label="Coverage">
            <el-select v-model="filters.coverage" style="width: 130px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option label="Weak (partial+none)" value="weak" />
              <el-option label="Good" value="good" />
              <el-option label="Partial" value="partial" />
              <el-option label="None" value="none" />
            </el-select>
          </el-form-item>
          <el-form-item label="Feedback">
            <el-select v-model="filters.rating" style="width: 110px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option label="👍 Up" value="up" />
              <el-option label="👎 Down" value="down" />
              <el-option label="No rating" value="none" />
            </el-select>
          </el-form-item>
          <el-form-item label="Clicked">
            <el-select v-model="filters.clicked" style="width: 100px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option label="Yes" value="yes" />
              <el-option label="No" value="no" />
            </el-select>
          </el-form-item>
          <el-form-item label="Outside recs">
            <el-select v-model="filters.external" style="width: 100px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option label="Yes" value="yes" />
              <el-option label="No" value="no" />
            </el-select>
          </el-form-item>
          <el-form-item label="Review">
            <el-select v-model="filters.review_status" style="width: 130px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option v-for="opt in reviewOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="Who">
            <el-select v-model="filters.identity" style="width: 120px" @change="searchTurns">
              <el-option label="All" value="" />
              <el-option label="Signed in" value="user" />
              <el-option label="Anonymous" value="anonymous" />
            </el-select>
          </el-form-item>
          <el-form-item v-if="filters.topic" label="Topic">
            <el-tag closable size="small" @close="clearTopic">{{ filters.topic }}</el-tag>
          </el-form-item>
          <el-form-item>
            <el-input
              v-model="filters.search"
              clearable
              placeholder="Question, need or answer text"
              style="width: 260px"
              @clear="searchTurns"
              @keyup.enter.native="searchTurns"
            >
              <el-button slot="append" icon="el-icon-search" @click="searchTurns" />
            </el-input>
          </el-form-item>
        </el-form>

        <el-table v-loading="turnsLoading" :data="turns" border size="small" style="width: 100%" @row-dblclick="openConversation">
          <el-table-column label="Time" width="140">
            <template slot-scope="scope">
              {{ formatDate(scope.row.created_at) }}
              <div v-if="scope.row.turn_index > 0" class="muted tiny">follow-up #{{ scope.row.turn_index + 1 }}</div>
            </template>
          </el-table-column>
          <el-table-column label="Question" min-width="300">
            <template slot-scope="scope">
              <div class="question">{{ scope.row.question }}</div>
              <div v-if="scope.row.user_need" class="muted tiny">{{ scope.row.user_need }}</div>
              <div v-if="hasItems(scope.row.topic_tags)" class="tags">
                <el-tag v-for="tag in scope.row.topic_tags" :key="tag" size="mini" type="info" class="tag" @click="filterByTopic(tag)">{{ tag }}</el-tag>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Coverage" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="coverageTag(scope.row.library_coverage)">{{ coverageLabel(scope.row.library_coverage) }}</el-tag>
              <div v-if="scope.row.generation_ok === false" class="muted tiny danger">LLM failed</div>
            </template>
          </el-table-column>
          <el-table-column label="Cited / Outside" width="110" align="center">
            <template slot-scope="scope">{{ scope.row.source_count }} / {{ scope.row.external_count }}</template>
          </el-table-column>
          <el-table-column label="Clicks" prop="click_count" width="70" align="center" />
          <el-table-column label="Feedback" width="120">
            <template slot-scope="scope">
              <span v-if="scope.row.feedback && scope.row.feedback.rating">
                {{ scope.row.feedback.rating === 'up' ? '👍' : '👎' }}
                <span v-if="scope.row.feedback.reason" class="muted tiny">{{ scope.row.feedback.reason }}</span>
              </span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="Review" width="120">
            <template slot-scope="scope">
              <el-tag size="mini" :type="reviewTag(reviewStatus(scope.row))">{{ reviewLabel(reviewStatus(scope.row)) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="User" min-width="160">
            <template slot-scope="scope">
              <template v-if="scope.row.user">
                <div>{{ scope.row.user.user_email || scope.row.user.user_name || scope.row.user_id }}</div>
                <div class="muted tiny">{{ scope.row.user.user_name }}</div>
              </template>
              <span v-else class="muted">Anonymous</span>
            </template>
          </el-table-column>
          <el-table-column label="Time taken" width="90" align="right">
            <template slot-scope="scope">{{ seconds(scope.row.timings && scope.row.timings.total_ms) }}</template>
          </el-table-column>
          <el-table-column label="" width="90" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" plain @click="openConversation(scope.row)">Review</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :current-page.sync="page"
            :page-size.sync="pageSize"
            :page-sizes="[20, 50, 100]"
            :total="total"
            @size-change="fetchTurns"
            @current-change="fetchTurns"
          />
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- ============================== Conversation drawer ============================== -->
    <el-drawer :visible.sync="drawerVisible" size="62%" :with-header="false" direction="rtl">
      <div v-loading="conversationLoading" class="drawer">
        <template v-if="conversation">
          <div class="drawer-head">
            <div>
              <div class="drawer-title">{{ conversation.title || 'Conversation' }}</div>
              <div class="muted tiny">
                {{ conversation.user ? (conversation.user.user_email || conversation.user.user_name) : 'Anonymous visitor ' + (conversation.visitor_id || '').slice(0, 8) }}
                · started {{ formatDate(conversation.created_at) }}
                · {{ conversationTurns.length }} turn(s)
                <span v-if="conversation.country && (conversation.country.selected_country || conversation.country.ip_country)">
                  · {{ conversation.country.selected_country || conversation.country.ip_country }}
                </span>
                <span v-if="conversation.interface_language">· UI {{ conversation.interface_language }}</span>
              </div>
            </div>
            <el-button size="small" icon="el-icon-close" circle @click="drawerVisible = false" />
          </div>

          <div
            v-for="turn in conversationTurns"
            :key="turn.turn_id"
            class="turn"
            :class="{ focused: turn.turn_id === focusTurnId }"
          >
            <div class="turn-question">
              <span class="turn-index">Q{{ turn.turn_index + 1 }}</span>
              {{ turn.question }}
            </div>
            <div class="turn-meta muted tiny">
              {{ formatDate(turn.created_at) }} · intent {{ turn.intent || '—' }} · reply {{ turn.reply_language || '—' }} ·
              {{ turn.model || 'no model' }} · {{ seconds(turn.timings && turn.timings.total_ms) }}
              (rewrite {{ seconds(turn.timings && turn.timings.rewrite_ms) }},
              retrieval {{ seconds(turn.timings && turn.timings.retrieval_ms) }},
              answer {{ seconds(turn.timings && turn.timings.answer_ms) }})
            </div>
            <div class="turn-meta muted tiny">
              Search queries:
              <code v-for="q in turn.search_queries || []" :key="q" class="query">{{ q }}</code>
            </div>
            <div v-if="turn.retrieval && turn.retrieval.stats" class="turn-meta muted tiny">
              Retrieved {{ turn.retrieval.stats.candidates }} ({{ turn.retrieval.stats.collections }} collections,
              {{ turn.retrieval.stats.modules }} modules, {{ turn.retrieval.stats.sessions }} sessions) ·
              {{ turn.retrieval.stats.strong_count }} strong · top score {{ turn.retrieval.stats.top_score }}
              <el-tag size="mini" :type="coverageTag(turn.library_coverage)" class="inline-tag">{{ coverageLabel(turn.library_coverage) }}</el-tag>
            </div>

            <div class="answer markdown" v-html="renderAnswer(turn.answer)" />

            <div v-if="hasItems(turn.sources)" class="block">
              <div class="block-title">Cited library items</div>
              <div v-for="src in turn.sources" :key="src.ref" class="src-row">
                <el-tag size="mini" type="info">{{ src.ref }}</el-tag>
                <a :href="siteUrl(src.path)" target="_blank" rel="noopener" class="item-link">{{ src.title }}</a>
                <span class="muted tiny">{{ src.type }}<span v-if="src.collection_title"> · {{ src.collection_title }}</span> · score {{ src.score }}</span>
                <el-tag v-if="wasClicked(turn, src)" size="mini" type="success">clicked</el-tag>
              </div>
            </div>

            <div v-if="hasItems(turn.external_recommendations)" class="block">
              <div class="block-title">Outside recommendations</div>
              <div v-for="rec in turn.external_recommendations" :key="rec.title" class="src-row">
                <el-tag size="mini">{{ rec.type || 'book' }}</el-tag>
                <strong>{{ rec.title }}</strong>
                <span class="muted tiny">{{ rec.author }} {{ rec.year }}</span>
                <span class="muted tiny">— {{ rec.reason }}</span>
              </div>
            </div>

            <details v-if="turn.retrieval && hasItems(turn.retrieval.candidates)" class="block">
              <summary class="block-title">All retrieved candidates ({{ turn.retrieval.candidates.length }})</summary>
              <el-table :data="turn.retrieval.candidates" size="mini" max-height="260">
                <el-table-column label="Ref" prop="ref" width="60" />
                <el-table-column label="Type" prop="type" width="90" />
                <el-table-column label="Title" prop="title" min-width="260" show-overflow-tooltip />
                <el-table-column label="Score" prop="score" width="80" align="right" />
              </el-table>
            </details>

            <div class="block feedback-line">
              <span class="block-title">User</span>
              <span v-if="turn.feedback && turn.feedback.rating">
                {{ turn.feedback.rating === 'up' ? '👍 helpful' : '👎 not helpful' }}
                <span v-if="turn.feedback.reason">({{ turn.feedback.reason }})</span>
                <em v-if="turn.feedback.comment">“{{ turn.feedback.comment }}”</em>
              </span>
              <span v-else class="muted">no rating</span>
              <span class="muted">· {{ turn.click_count || 0 }} click(s)</span>
              <span v-if="hasItems(turn.follow_up_questions)" class="muted tiny">· suggested: {{ turn.follow_up_questions.join(' / ') }}</span>
            </div>

            <div class="review-box">
              <el-radio-group v-model="reviewDrafts[turn.turn_id].status" size="mini">
                <el-radio-button v-for="opt in reviewOptions" :key="opt.value" :label="opt.value">{{ opt.label }}</el-radio-button>
              </el-radio-group>
              <el-input
                v-model="reviewDrafts[turn.turn_id].note"
                type="textarea"
                :rows="2"
                maxlength="2000"
                placeholder="Review note — what should retrieval / prompt / content do differently?"
                class="review-note"
              />
              <div class="review-actions">
                <span v-if="turn.review && turn.review.at" class="muted tiny">
                  Last saved {{ formatDate(turn.review.at) }}<span v-if="turn.review.reviewer_name"> by {{ turn.review.reviewer_name }}</span>
                </span>
                <el-button size="mini" type="primary" :loading="savingReviewId === turn.turn_id" @click="saveReview(turn)">Save review</el-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  generateAiSearchContentIdeas,
  getAiSearchConversation,
  getAiSearchInsights,
  listAiSearchContentIdeaReports,
  listAiSearchTurns,
  updateAiSearchTurnReview
} from '@/apis/lfAdmin'

const SITE_URL = 'https://learningfirst.io'
const REVIEWER_KEY = 'lf_admin_reviewer_name'

export default {
  name: 'AiSearchReview',
  data() {
    return {
      activeTab: 'insights',
      dateRange: [dayjs().subtract(29, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      pickerOptions: {
        shortcuts: [
          { text: 'Last 7 days', onClick: (picker) => picker.$emit('pick', [dayjs().subtract(6, 'day').toDate(), new Date()]) },
          { text: 'Last 30 days', onClick: (picker) => picker.$emit('pick', [dayjs().subtract(29, 'day').toDate(), new Date()]) },
          { text: 'Last 90 days', onClick: (picker) => picker.$emit('pick', [dayjs().subtract(89, 'day').toDate(), new Date()]) }
        ]
      },
      insightsLoading: false,
      insights: null,
      hoverDay: null,
      ideasLoading: false,
      ideaLanguage: 'zh',
      ideaReports: [],
      activeReportId: '',
      turnsLoading: false,
      turns: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        coverage: '',
        rating: '',
        clicked: '',
        external: '',
        review_status: '',
        identity: '',
        topic: '',
        search: ''
      },
      reviewOptions: [
        { value: 'unreviewed', label: 'Unreviewed' },
        { value: 'good', label: 'Good' },
        { value: 'needs_fix', label: 'Needs fix' },
        { value: 'content_gap', label: 'Content gap' },
        { value: 'ignored', label: 'Ignore' }
      ],
      drawerVisible: false,
      conversationLoading: false,
      conversation: null,
      conversationTurns: [],
      focusTurnId: '',
      reviewDrafts: {},
      savingReviewId: '',
      reviewer: ''
    }
  },
  computed: {
    dateParams() {
      if (!this.dateRange || this.dateRange.length !== 2) return {}
      return {
        date_from: dayjs(this.dateRange[0]).startOf('day').unix(),
        date_to: dayjs(this.dateRange[1]).endOf('day').unix()
      }
    },
    kpiTiles() {
      if (!this.insights) return []
      const t = this.insights.totals
      const c = this.insights.coverage
      const weak = (c.partial || 0) + (c.none || 0)
      const rated = (t.feedback_up || 0) + (t.feedback_down || 0)
      return [
        { label: 'Questions', value: this.num(t.turns), hint: `${this.num(t.conversations)} conversations` },
        { label: 'Learners', value: this.num(t.users), hint: `${this.num(t.anonymous_turns)} anonymous questions` },
        { label: 'Clicked through', value: this.percent(t.turns_with_click, t.turns), hint: `${this.num(t.clicks)} clicks to library pages` },
        { label: 'Weak coverage', value: this.percent(weak, t.turns), hint: `${this.num(t.turns_with_external)} answers added outside books` },
        { label: 'Helpful', value: rated ? this.percent(t.feedback_up, rated) : '—', hint: `👍 ${t.feedback_up} · 👎 ${t.feedback_down}` },
        { label: 'Avg answer time', value: this.seconds(t.avg_total_ms), hint: t.generation_errors ? `${t.generation_errors} LLM failures` : 'no LLM failures' }
      ]
    },
    dailySeries() {
      if (!this.insights || !this.insights.totals.turns) return []
      // Backend only returns days that had questions; fill the gaps with zeros
      // so the time axis stays honest.
      const byDate = {}
      const daily = this.insights.daily || []
      daily.forEach((d) => {
        byDate[d.date] = d
      })
      const end = dayjs.unix(this.insights.date_to).startOf('day')
      const days = []
      for (let d = dayjs.unix(this.insights.date_from).startOf('day'); !d.isAfter(end) && days.length < 400; d = d.add(1, 'day')) {
        const key = d.format('YYYY-MM-DD')
        days.push(byDate[key] || { date: key, turns: 0, weak: 0 })
      }
      return days
    },
    dailyMax() {
      return this.dailySeries.reduce((max, d) => Math.max(max, d.turns), 0)
    },
    coverageRows() {
      if (!this.insights) return []
      const c = this.insights.coverage
      const total = (c.good || 0) + (c.partial || 0) + (c.none || 0)
      return [
        { key: 'good', label: 'Good', tag: 'success', count: c.good || 0 },
        { key: 'partial', label: 'Partial', tag: 'warning', count: c.partial || 0 },
        { key: 'none', label: 'None', tag: 'danger', count: c.none || 0 }
      ].map((row) => ({ ...row, pct: total ? Math.round((row.count / total) * 100) : 0 }))
    },
    activeReport() {
      if (!this.ideaReports.length) return null
      return this.ideaReports.find((r) => r.report_id === this.activeReportId) || this.ideaReports[0]
    }
  },
  mounted() {
    this.reviewer = this.reviewerName()
    this.refreshAll()
    this.fetchReports()
  },
  methods: {
    refreshAll() {
      this.fetchInsights()
      this.page = 1
      this.fetchTurns()
    },
    async fetchInsights() {
      this.insightsLoading = true
      try {
        const res = await getAiSearchInsights(this.dateParams)
        if (res.data && res.data.code === 0) {
          this.insights = res.data.data
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to load insights')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load insights')
      } finally {
        this.insightsLoading = false
      }
    },
    async fetchReports() {
      try {
        const res = await listAiSearchContentIdeaReports({ limit: 20 })
        if (res.data && res.data.code === 0) {
          this.ideaReports = res.data.data.items || []
          this.activeReportId = this.ideaReports.length ? this.ideaReports[0].report_id : ''
        }
      } catch (error) {
        console.error(error)
      }
    },
    async generateIdeas() {
      this.ideasLoading = true
      try {
        const res = await generateAiSearchContentIdeas({
          ...this.dateParams,
          language: this.ideaLanguage,
          reviewer_name: this.reviewerName()
        })
        if (res.data && res.data.code === 0) {
          this.$message.success('Content ideas generated')
          await this.fetchReports()
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to generate ideas')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to generate ideas')
      } finally {
        this.ideasLoading = false
      }
    },
    searchTurns() {
      this.page = 1
      this.fetchTurns()
    },
    async fetchTurns() {
      this.turnsLoading = true
      try {
        const params = { page: this.page, page_size: this.pageSize, ...this.dateParams }
        Object.keys(this.filters).forEach((key) => {
          const value = typeof this.filters[key] === 'string' ? this.filters[key].trim() : this.filters[key]
          if (value) params[key] = value
        })
        const res = await listAiSearchTurns(params)
        if (res.data && res.data.code === 0) {
          this.turns = res.data.data.items || []
          this.total = res.data.data.total || 0
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to load conversations')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load conversations')
      } finally {
        this.turnsLoading = false
      }
    },
    filterByTopic(tag) {
      this.filters.topic = tag
      this.activeTab = 'turns'
      this.searchTurns()
    },
    clearTopic() {
      this.filters.topic = ''
      this.searchTurns()
    },
    async openConversation(row) {
      this.drawerVisible = true
      this.conversationLoading = true
      this.conversation = null
      this.conversationTurns = []
      this.focusTurnId = row.turn_id
      try {
        const res = await getAiSearchConversation({ conversation_id: row.conversation_id })
        if (res.data && res.data.code === 0) {
          const drafts = {}
          const turns = res.data.data.turns || []
          turns.forEach((turn) => {
            drafts[turn.turn_id] = {
              status: (turn.review && turn.review.status) || 'unreviewed',
              note: (turn.review && turn.review.note) || ''
            }
          })
          this.reviewDrafts = drafts
          this.conversation = res.data.data.conversation
          this.conversationTurns = turns
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to load conversation')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load conversation')
      } finally {
        this.conversationLoading = false
      }
    },
    async saveReview(turn) {
      const draft = this.reviewDrafts[turn.turn_id]
      if (!draft) return
      this.savingReviewId = turn.turn_id
      try {
        const res = await updateAiSearchTurnReview({
          turn_id: turn.turn_id,
          review_status: draft.status,
          review_note: draft.note,
          reviewer_name: this.reviewerName()
        })
        if (res.data && res.data.code === 0) {
          this.$set(turn, 'review', res.data.data)
          const row = this.turns.find((t) => t.turn_id === turn.turn_id)
          if (row) this.$set(row, 'review', res.data.data)
          this.$message.success('Review saved')
        } else {
          this.$message.error((res.data && res.data.msg) || 'Failed to save review')
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to save review')
      } finally {
        this.savingReviewId = ''
      }
    },
    reviewerName() {
      if (this.reviewer) return this.reviewer.trim()
      try {
        return window.localStorage.getItem(REVIEWER_KEY) || ''
      } catch (e) {
        return ''
      }
    },
    saveReviewerName() {
      try {
        window.localStorage.setItem(REVIEWER_KEY, (this.reviewer || '').trim())
      } catch (e) {
        // ignore — the name is only a convenience for review attribution
      }
    },
    renderAnswer(markdown) {
      if (!markdown) return ''
      // Answers contain site-relative links; make them absolute so reviewers can open them.
      const html = marked.parse(String(markdown), { breaks: true, gfm: true })
        .replace(/href="\/(?!\/)/g, `href="${SITE_URL}/`)
      return DOMPurify.sanitize(html, { ADD_ATTR: ['target'] }).replace(/<a /g, '<a target="_blank" rel="noopener" ')
    },
    siteUrl(path) {
      if (!path) return SITE_URL
      return path.startsWith('/') ? SITE_URL + path : path
    },
    wasClicked(turn, src) {
      return (turn.clicks || []).some((c) => c.ref === src.ref || c.path === src.path)
    },
    reviewStatus(row) {
      return (row.review && row.review.status) || 'unreviewed'
    },
    reviewLabel(status) {
      const opt = this.reviewOptions.find((o) => o.value === status)
      return opt ? opt.label : status
    },
    reviewTag(status) {
      return { good: 'success', needs_fix: 'danger', content_gap: 'warning', ignored: 'info' }[status] || ''
    },
    coverageTag(coverage) {
      return { good: 'success', partial: 'warning', none: 'danger' }[coverage] || 'info'
    },
    coverageLabel(coverage) {
      return { good: 'Good', partial: 'Partial', none: 'None' }[coverage] || '—'
    },
    priorityTag(priority) {
      return { high: 'danger', medium: 'warning', low: 'info' }[priority] || 'info'
    },
    barHeight(value) {
      if (!this.dailyMax) return '0%'
      return `${Math.max(2, Math.round((value / this.dailyMax) * 100))}%`
    },
    hasItems(list) {
      return Array.isArray(list) && list.length > 0
    },
    num(value) {
      return Number(value || 0).toLocaleString()
    },
    percent(part, whole) {
      if (!whole) return '0%'
      return `${Math.round((part / whole) * 100)}%`
    },
    seconds(ms) {
      if (!ms && ms !== 0) return '—'
      return `${(ms / 1000).toFixed(1)}s`
    },
    formatDate(ts) {
      return ts ? dayjs.unix(ts).format('YYYY-MM-DD HH:mm') : '-'
    },
    formatDay(ts) {
      return ts ? dayjs.unix(ts).format('YYYY-MM-DD') : '-'
    }
  }
}
</script>

<style scoped lang="scss">
.ai-review {
  padding: 20px 24px 40px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;

  h2 {
    margin: 0;
    font-size: 20px;
    color: #303133;
  }
}

.subtitle {
  margin-top: 4px;
  max-width: 720px;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.muted {
  color: #909399;
}

.tiny {
  font-size: 12px;
}

.danger {
  color: #f56c6c;
}

.strong {
  font-weight: 600;
  color: #303133;
}

.empty {
  padding: 24px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.kpi {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 14px;
}

.kpi-label {
  font-size: 12px;
  color: #909399;
}

.kpi-value {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  font-variant-numeric: tabular-nums;
}

.kpi-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

.truncated-note {
  margin-bottom: 12px;
}

.panel {
  margin-bottom: 16px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  color: #303133;
}

.panel-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: normal;
}

/* Daily volume: single series, thin bars with 2px gaps, rounded data end. */
.bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 140px;
  border-bottom: 1px solid #dcdfe6;
}

.bar-slot {
  flex: 1 1 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
  cursor: default;

  &:hover .bar {
    background: #1d6fd1;
  }
}

.bar {
  width: 100%;
  max-width: 18px;
  margin: 0 auto;
  background: #409eff;
  border-radius: 4px 4px 0 0;
}

.bar-axis {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  min-height: 18px;
}

.bar-tooltip {
  color: #303133;
}

.coverage-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.coverage-tag {
  width: 64px;
  text-align: center;
}

.coverage-track {
  flex: 1;
  height: 8px;
  background: #f2f6fc;
  border-radius: 4px;
  overflow: hidden;
}

.coverage-fill {
  height: 100%;
  border-radius: 4px;

  &.fill-good { background: #67c23a; }
  &.fill-partial { background: #e6a23c; }
  &.fill-none { background: #f56c6c; }
}

.coverage-num {
  width: 90px;
  text-align: right;
  font-size: 12px;
  color: #606266;
  font-variant-numeric: tabular-nums;
}

.coverage-meta {
  margin-top: 8px;
}

.review-count {
  margin-left: 8px;
}

.idea-summary {
  margin: 10px 0 14px;
  line-height: 1.6;
  color: #303133;
  white-space: pre-wrap;
}

.idea-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.idea {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafbfc;

  details {
    margin-top: 6px;
    font-size: 12px;
    color: #606266;
  }

  summary {
    cursor: pointer;
    color: #409eff;
  }

  ul, ol {
    margin: 4px 0 0;
    padding-left: 18px;
    line-height: 1.6;
  }
}

.idea-top {
  display: flex;
  gap: 6px;
  align-items: center;
}

.idea-title {
  margin-top: 6px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.idea-text {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #606266;
}

.example-line {
  font-size: 12px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-link {
  color: #409eff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.filters {
  margin-bottom: 4px;
}

.question {
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.tags {
  margin-top: 4px;
}

.tag {
  margin-right: 4px;
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.drawer {
  padding: 20px 24px 40px;
  min-height: 100%;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.drawer-title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

.turn {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 14px;
  background: #fff;

  &.focused {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
  }
}

.turn-question {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.5;
}

.turn-index {
  display: inline-block;
  margin-right: 6px;
  padding: 0 6px;
  border-radius: 4px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 12px;
}

.turn-meta {
  margin-top: 4px;
  line-height: 1.6;
}

.query {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f4f4f5;
  color: #606266;
}

.inline-tag {
  margin-left: 6px;
}

.answer {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  background: #f7f8fa;
  font-size: 14px;
  line-height: 1.7;
  color: #303133;

  ::v-deep p {
    margin: 0 0 8px;
  }

  ::v-deep ul,
  ::v-deep ol {
    margin: 0 0 8px;
    padding-left: 20px;
  }

  ::v-deep a {
    color: #409eff;
    font-weight: 600;
  }
}

.block {
  margin-top: 12px;
}

.block-title {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 6px;
  cursor: default;
}

details.block > summary {
  cursor: pointer;
}

.src-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
}

.feedback-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  .block-title {
    margin-bottom: 0;
  }
}

.review-box {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e4e7ed;
}

.review-note {
  margin-top: 8px;
}

.review-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
</style>
